import React, { useState } from 'react';
import { 
  Calendar, Clock, ExternalLink, ChevronDown, ChevronUp,
  Newspaper, Globe, Shield, Star, Tag, Eye, Building2, DollarSign, Cpu, Users, FileText
} from 'lucide-react';
import { NewsArticle, NewsAnalysisRelation } from '../../types/enhancedAnalysis';

interface NewsContextSectionProps {
  newsArticle: NewsArticle;
  newsAnalysisRelation: NewsAnalysisRelation;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onViewOriginal: () => void;
}

const NewsContextSection: React.FC<NewsContextSectionProps> = ({
  newsArticle,
  newsAnalysisRelation,
  isExpanded,
  onToggleExpand,
  onViewOriginal
}) => {
  const [showFullSummary, setShowFullSummary] = useState(false);

  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'official': return <Building2 className="w-4 h-4 text-red-600" />;
      case 'finance': return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'tech': return <Cpu className="w-4 h-4 text-blue-600" />;
      case 'international': return <Globe className="w-4 h-4 text-purple-600" />;
      case 'youth': return <Users className="w-4 h-4 text-orange-600" />;
      case 'comprehensive': return <FileText className="w-4 h-4 text-gray-600" />;
      default: return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceTypeLabel = (type: string) => {
    switch (type) {
      case 'official': return '官方媒体';
      case 'finance': return '财经媒体';
      case 'tech': return '科技媒体';
      case 'international': return '国际媒体';
      case 'youth': return '青年媒体';
      case 'comprehensive': return '综合媒体';
      default: return type;
    }
  };



  const getCredibilityColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-50';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatPublishTime = (publishTime: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - publishTime.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '刚刚发布';
    if (diffInHours < 24) return `${diffInHours}小时前`;
    if (diffInHours < 48) return '昨天';
    
    return publishTime.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateNewsTags = () => {
    const tags = [];
    if (newsArticle.category) tags.push(newsArticle.category);
    tags.push(getSourceTypeLabel(newsArticle.source.type));
    
    const daysSincePublish = Math.floor(
      (Date.now() - newsArticle.publishTime.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSincePublish === 0) tags.push('今日');
    else if (daysSincePublish <= 3) tags.push('近期');
    
    return tags;
  };

  return (
    <section className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl mb-8 overflow-hidden">
      {/* 头部区域 */}
      <div className="p-6 border-b border-blue-200 bg-white/50">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">新闻背景</h3>
              <p className="text-sm text-gray-600">{newsAnalysisRelation.analysisAngle}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCredibilityColor(newsArticle.source.credibilityScore)}`}>
              可信度 {Math.round(newsArticle.source.credibilityScore * 100)}%
            </span>
            <button
              onClick={onToggleExpand}
              className="p-2 rounded-lg hover:bg-blue-100 transition-colors"
              aria-label={isExpanded ? "收起新闻内容" : "展开新闻内容"}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* 新闻标题 */}
        <h2 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
          {newsArticle.title}
        </h2>

        {/* 新闻元信息 */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
          {/* 来源信息 */}
          <div className="flex items-center space-x-2">
            {getSourceTypeIcon(newsArticle.source.type)}
            <span className="font-medium">{newsArticle.source.name}</span>
          </div>

          {/* 发布时间 */}
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4" />
            <span>{formatPublishTime(newsArticle.publishTime)}</span>
          </div>

          {/* 阅读时间 */}
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>约 {newsArticle.readingTime} 分钟阅读</span>
          </div>
        </div>

        {/* 新闻标签 */}
        <div className="flex flex-wrap gap-2 mb-4">
          {generateNewsTags().map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
            >
              <Tag className="w-3 h-3 mr-1" />
              {tag}
            </span>
          ))}
        </div>

        {/* 新闻摘要 */}
        <div className="bg-white rounded-lg p-4 border border-blue-100">
          <p className="text-gray-700 leading-relaxed">
            {showFullSummary ? newsArticle.summary : newsArticle.summary.slice(0, 200)}
            {newsArticle.summary.length > 200 && !showFullSummary && '...'}
          </p>
          
          {newsArticle.summary.length > 200 && (
            <button
              onClick={() => setShowFullSummary(!showFullSummary)}
              className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
            >
              {showFullSummary ? '收起' : '展开完整摘要'}
            </button>
          )}
        </div>

        {/* 快速操作 */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={onViewOriginal}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              查看原文
            </button>
            
            {/* 移除分享新闻按钮 */}
          </div>

          <div className="text-xs text-gray-500">
            相关性评分: {Math.round(newsAnalysisRelation.relevanceScore * 100)}%
          </div>
        </div>
      </div>

      {/* 展开内容区域 */}
      {isExpanded && (
        <div className="p-6 bg-white/30">
          {/* 关键关联点 */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">分析关联点</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {newsAnalysisRelation.keyConnectionPoints.map((point, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 背景上下文 */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">背景说明</h4>
            <p className="text-gray-700 leading-relaxed bg-white rounded-lg p-4 border border-blue-100">
              {newsAnalysisRelation.backgroundContext}
            </p>
          </div>

          {/* 新闻完整内容预览 */}
          {newsArticle.content && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">新闻内容预览</h4>
              <div className="bg-white rounded-lg p-4 border border-blue-100 max-h-64 overflow-y-auto">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {newsArticle.content.slice(0, 500)}
                  {newsArticle.content.length > 500 && '...'}
                </p>
                {newsArticle.content.length > 500 && (
                  <button
                    onClick={onViewOriginal}
                    className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    查看完整新闻内容 →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 来源详细信息 */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="text-md font-semibold text-gray-900 mb-3">来源信息</h4>
            <div className="flex items-start space-x-4">
              {newsArticle.source.logo && (
                <img
                  src={newsArticle.source.logo}
                  alt={newsArticle.source.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h5 className="font-medium text-gray-900">{newsArticle.source.name}</h5>
                  {getSourceTypeIcon(newsArticle.source.type)}
                  <span className="text-sm text-gray-600">
                    {getSourceTypeLabel(newsArticle.source.type)}
                  </span>
                </div>
                {newsArticle.source.description && (
                  <p className="text-sm text-gray-600 mb-2">{newsArticle.source.description}</p>
                )}
                {newsArticle.source.website && (
                  <a
                    href={newsArticle.source.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    访问官网
                  </a>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-1 mb-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">
                    {(newsArticle.source.credibilityScore * 5).toFixed(1)}
                  </span>
                </div>
                <div className="text-xs text-gray-500">可信度评分</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 底部分隔线 */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
    </section>
  );
};

export default NewsContextSection;